// ─── Types ─────────────────────────────────────────────────────

export type DragType = 'create' | 'move' | 'resize-start' | 'resize-end';

export interface DragState {
  type: DragType;
  entryId?: string;           // for move/resize (undefined for create)
  originDay: number;          // 0–6 (Mon–Sun)
  originMinute: number;       // minutes since midnight, snapped to 15
  currentDay: number;
  currentMinute: number;
  /** Original entry start/end for move/resize (minutes since midnight) */
  entryStartMinute?: number;
  entryEndMinute?: number;
  committed: boolean;         // true once drag exceeds 15-min threshold
}

export interface DragResult {
  day: number;                // 0–6
  startMinute: number;        // snapped
  endMinute: number;          // snapped
}

// ─── Constants ─────────────────────────────────────────────────

const SNAP = 15;              // minutes
const MIN_DURATION = 15;      // minimum entry = 15 minutes

// ─── Snap helper ───────────────────────────────────────────────

export function snapMinute(m: number): number {
  return Math.round(m / SNAP) * SNAP;
}

// ─── Convert pointer Y to minutes ──────────────────────────────

/**
 * Convert a Y pixel position within the grid to minutes since midnight.
 * hourHeight is the pixel height of one hour (HOUR_HEIGHT).
 */
export function yToMinute(y: number, hourHeight: number): number {
  return snapMinute(Math.max(0, Math.min(1440, (y / hourHeight) * 60)));
}

/**
 * Convert a pointer's viewport `clientY` to minutes since midnight.
 *
 * `gridRectTop` MUST be the top of the scrolling grid *content*
 * (`getBoundingClientRect().top`), not the scroll container. Because the
 * content scrolls inside its container, its rect.top already reflects the
 * current scroll position (`containerTop − scrollTop`). Subtracting it from
 * `clientY` therefore yields the content-relative Y directly — scrollTop must
 * NOT be added again or the position is inflated by one scroll height.
 */
export function clientYToMinute(
  clientY: number,
  gridRectTop: number,
  hourHeight: number,
): number {
  return yToMinute(clientY - gridRectTop, hourHeight);
}

/**
 * Convert a pointer X position within the grid body (excluding the
 * hour gutter) to a day index 0–6.
 * gridBodyWidth is the width of the 7-column area.
 */
export function xToDay(x: number, gridBodyWidth: number, dayCount: number): number {
  const col = Math.floor((x / gridBodyWidth) * dayCount);
  return Math.max(0, Math.min(dayCount - 1, col));
}

// ─── State machine helpers ─────────────────────────────────────

/**
 * Start a new drag operation. Returns the initial DragState.
 */
export function startDrag(
  type: DragType,
  day: number,
  minute: number,
  entryId?: string,
  entryStartMinute?: number,
  entryEndMinute?: number,
): DragState {
  const snapped = snapMinute(minute);
  return {
    type,
    entryId,
    originDay: day,
    originMinute: snapped,
    currentDay: day,
    currentMinute: snapped,
    entryStartMinute,
    entryEndMinute,
    committed: false,
  };
}

/**
 * Update drag state with new pointer position.
 * Returns a new DragState (immutable update).
 */
export function updateDrag(state: DragState, day: number, minute: number): DragState {
  const snapped = snapMinute(minute);
  const committed =
    state.committed ||
    Math.abs(snapped - state.originMinute) >= SNAP ||
    day !== state.originDay;
  return {
    ...state,
    currentDay: day,
    currentMinute: snapped,
    committed,
  };
}

/**
 * Compute the final result from a completed drag.
 * Returns the day, startMinute, and endMinute for the created/modified entry.
 */
export function finalizeDrag(state: DragState): DragResult {
  switch (state.type) {
    case 'create': {
      const min = Math.min(state.originMinute, state.currentMinute);
      const max = Math.max(state.originMinute, state.currentMinute);
      const duration = max - min;
      // If drag was too short (click), create a 30-min default entry
      const startMinute = duration < MIN_DURATION ? state.originMinute : min;
      const endMinute = duration < MIN_DURATION ? state.originMinute + 30 : max;
      return { day: state.currentDay, startMinute, endMinute };
    }
    case 'move': {
      const entryDuration = (state.entryEndMinute ?? 0) - (state.entryStartMinute ?? 0);
      const deltaMinute = state.currentMinute - state.originMinute;
      let newStart = (state.entryStartMinute ?? 0) + deltaMinute;
      // Clamp to 0–1440
      newStart = Math.max(0, Math.min(1440 - entryDuration, newStart));
      return {
        day: state.currentDay,
        startMinute: newStart,
        endMinute: newStart + entryDuration,
      };
    }
    case 'resize-start': {
      const newStart = Math.min(state.currentMinute, (state.entryEndMinute ?? 60) - MIN_DURATION);
      return {
        day: state.originDay,
        startMinute: Math.max(0, newStart),
        endMinute: state.entryEndMinute ?? 60,
      };
    }
    case 'resize-end': {
      const newEnd = Math.max(state.currentMinute, (state.entryStartMinute ?? 0) + MIN_DURATION);
      return {
        day: state.originDay,
        startMinute: state.entryStartMinute ?? 0,
        endMinute: Math.min(1440, newEnd),
      };
    }
  }
}
